import { PURGE } from 'redux-persist';

const initialState = {
  // Users
  adminUsers: [],
  deletedUsers: [],
  adminUserStats: {},
  usersPagination: { page: 1, limit: 20, total: 0, pages: 0 },
  deletedUsersPagination: { page: 1, limit: 20, total: 0, pages: 0 },
  
  // Workspaces
  adminWorkspaces: [],
  deletedWorkspaces: [],
  availableWorkspaces: [],
  workspacePlans: [],
  hasStripeConfigured: false,
  plansMessage: '',
  workspaceStats: {},
  workspacesPagination: { page: 1, limit: 20, total: 0, pages: 0 },
  deletedWorkspacesPagination: { page: 1, limit: 20, total: 0, pages: 0 },
  selectedWorkspaceDetails: null,
  selectedWorkspaceActivity: { activities: [], pagination: { page: 1, limit: 20, total: 0, pages: 0 } },
  
  // Feedback
  adminFeedback: [],
  feedbackStats: {},
  feedbackPagination: { page: 1, limit: 20, total: 0, pages: 0 },
  selectedFeedback: null,
  loadingFeedback: false,

  // Workspace Details Sidebar
  workspaceMembers: [],
  workspaceContacts: [],
  workspaceDeals: [],
  workspaceAgents: [],
  workspaceActivity: [],
  ownerWorkspaces: [],
  ownerWorkspacesData: null,
  accountAgentsData: null,
  workspacePaymentHistory: null,
  loadingMembers: false,
  loadingContacts: false,
  loadingDeals: false,
  loadingAgents: false,
  loadingActivity: false,
  loadingOwnerWorkspaces: false,
  loadingAccountAgents: false,
  loadingPaymentHistory: false,
  
  // User Details Sidebar
  userWorkspaces: [],
  userBillingHistory: {},
  userSecurityLogs: [],
  activityLogs: [],
  loadingUserWorkspaces: false,
  loadingUserBilling: false,
  loadingUserSecurity: false,
  loadingUserActivities: false,
  
  // Billing
  adminSubscriptions: [],
  subscriptionStats: {},
  selectedSubscription: null,
  payments: [],
  workspaceSummary: null,
  revenueAnalytics: {},
  mrrAnalytics: {},
  plansOverview: { plans: [], totals: {}, mostPopularPlan: null },
  adminPlans: [],
  plansPagination: { page: 1, limit: 20, total: 0, pages: 0 },
  subscriptionsPagination: { page: 1, limit: 20, total: 0, pages: 0 },
  paymentsPagination: { page: 1, limit: 20, total: 0, pages: 0 },
  
  // Subscription Details Sidebar
  subscriptionPayments: [],
  subscriptionActivity: [],
  subscriptionUsageHistory: [],
  loadingPayments: false,
  loadingActivity: false,
  loadingUsageHistory: false,
  
  // Agents
  adminAgents: [],
  agentStats: {},
  selectedAgent: null,
  agentLogs: [],
  agentCalls: [],
  performanceMetrics: {},
  costAnalytics: {},
  agentsPagination: { page: 1, limit: 20, total: 0, pages: 0 },
  
  // System Monitoring
  systemHealth: null,
  systemServices: {},
  systemMetrics: [],
  systemAlerts: [],
  systemPerformance: {},
  
  // Dashboard
  adminDashboard: {},
  
  // Analytics
  analyticsData: {},
  customReports: [],
  
  // Security & Compliance
  auditLogs: [],
  auditLogsPagination: { page: 1, limit: 50, total: 0, pages: 0 },
  securitySettings: {},
  apiKeys: [],
  ipWhitelist: [],
  
  // Compliance
  complianceStatus: {
    gdpr: {},
    soc2: {},
    dataRetention: [],
    complianceReports: [],
    dataRequests: []
  },
  
  // Credit Management
  creditHistory: { transactions: [], stats: {}, pagination: { page: 1, limit: 20, total: 0, pages: 0 } },
  workspaces: [],
  workspaceCredits: { workspaces: [], stats: {} },

  // User Access Type
  userAccessType: { accessType: 'admin', workspaceId: null, user: null },
  
  // Email Templates
  emailTemplates: [],
  templateStats: {},
  templatesPagination: { page: 1, limit: 20, total: 0, pages: 0 },
  
  // Activity Logs
  activityLogs: [],
  activityStats: {},
  activityLogsPagination: { page: 1, limit: 50, total: 0, pages: 0 },
  
  // System Configuration
  systemConfig: {
    general: {},
    email: {},
    twilio: {},
    openai: {},
    stripe: {},
    features: {},
    limits: {}
  },
  
  // Backup & Restore
  backups: [],
  backupStats: {},
  restoreHistory: [],
  backupsPagination: { page: 1, limit: 20, total: 0, pages: 0 },
  restoreHistoryPagination: { page: 1, limit: 20, total: 0, pages: 0 },

  // User Feedback - COMMENTED OUT - feedback only stored in DB, no admin display
  // adminFeedback: [],
  // feedbackStats: {},
  // feedbackPagination: { page: 1, limit: 20, total: 0, pages: 0 },
  // selectedFeedback: null,

  // UI State
  loading: false,
  loadingMessage: '',
  reportGenerating: false,
  creditHistoryLoading: false,
};

export default function reducer(state = initialState, { type, payload }) {
  switch (type) {
    case PURGE:
      return initialState;

    // Loading States
    case 'GET_ADMIN_USERS':
    case 'GET_ADMIN_USER_STATS':
    case 'GET_ADMIN_WORKSPACES':
    case 'GET_WORKSPACE_STATS':
    case 'GET_ADMIN_SUBSCRIPTIONS':
    case 'GET_SUBSCRIPTION_STATS':
    case 'GET_ADMIN_AGENTS':
    case 'GET_AGENT_STATS':
    case 'GET_SYSTEM_HEALTH':
    case 'GET_SYSTEM_SERVICES':
    case 'GET_SYSTEM_METRICS':
    case 'GET_SYSTEM_ALERTS':
    case 'GET_SYSTEM_PERFORMANCE':
    case 'GET_ADMIN_DASHBOARD':
      return { ...state, loading: true };

    // User Management Reducers
    case 'SET_ADMIN_USERS':
      return { 
        ...state, 
        adminUsers: payload.users || [],
        usersPagination: payload.pagination || state.usersPagination,
        loading: false 
      };

    case 'SET_ADMIN_USER_STATS':
      return { ...state, adminUserStats: payload, loading: false };

    case 'SET_DELETED_USERS':
      return {
        ...state,
        deletedUsers: payload.deletedUsers || [],
        deletedUsersPagination: payload.deletedUsersPagination || state.deletedUsersPagination,
        loading: false
      };

    // Workspace Management Reducers
    case 'SET_ADMIN_WORKSPACES':
      return {
        ...state,
        adminWorkspaces: payload.workspaces || [],
        workspacesPagination: payload.pagination || state.workspacesPagination,
        loading: false
      };

    case 'SET_AVAILABLE_WORKSPACES':
      return {
        ...state,
        availableWorkspaces: payload || []
      };

    case 'SET_WORKSPACE_PLANS':
      return {
        ...state,
        workspacePlans: payload.plans || [],
        hasStripeConfigured: payload.hasStripeConfigured || false,
        plansMessage: payload.message || ''
      };

    case 'SET_WORKSPACE_STATS':
      return { ...state, workspaceStats: payload, loading: false };

    case 'SET_DELETED_WORKSPACES':
      return {
        ...state,
        deletedWorkspaces: payload.deletedWorkspaces || [],
        deletedWorkspacesPagination: payload.deletedWorkspacesPagination || state.deletedWorkspacesPagination,
        loading: false
      };

    case 'SET_SELECTED_WORKSPACE_DETAILS':
      return { ...state, selectedWorkspaceDetails: payload, loading: false };

    case 'SET_SELECTED_WORKSPACE_ACTIVITY':
      return { ...state, selectedWorkspaceActivity: payload, loading: false };

    // Billing Management Reducers
    case 'SET_ADMIN_SUBSCRIPTIONS':
      return { 
        ...state, 
        adminSubscriptions: payload.subscriptions || [],
        subscriptionsPagination: payload.pagination || state.subscriptionsPagination,
        loading: false 
      };

    case 'SET_SUBSCRIPTION_STATS':
      return { ...state, subscriptionStats: payload, loading: false };

    case 'SET_SELECTED_SUBSCRIPTION':
      return { ...state, selectedSubscription: payload };

    case 'SET_PAYMENTS':
      return { 
        ...state, 
        payments: payload.payments || [],
        paymentsPagination: payload.pagination || state.paymentsPagination,
        loading: false 
      };

    case 'SET_WORKSPACE_SUMMARY':
      return { ...state, workspaceSummary: payload, loading: false };

    case 'SET_REVENUE_ANALYTICS':
      return { ...state, revenueAnalytics: payload, loading: false };

    case 'SET_MRR_ANALYTICS':
      return { ...state, mrrAnalytics: payload, loading: false };

    case 'SET_PLANS_OVERVIEW':
      return { ...state, plansOverview: payload, loading: false };
    
    case 'SET_ADMIN_PLANS':
      return { ...state, adminPlans: payload.plans || payload, loading: false };
    
    // Subscription Details Sidebar Reducers
    case 'SET_SUBSCRIPTION_PAYMENTS':
      return { ...state, subscriptionPayments: payload, loadingPayments: false };
    
    case 'SET_SUBSCRIPTION_ACTIVITY':
      return { ...state, subscriptionActivity: payload, loadingActivity: false };
    
    case 'SET_SUBSCRIPTION_USAGE_HISTORY':
      return { ...state, subscriptionUsageHistory: payload, loadingUsageHistory: false };
    
    case 'LOADING_SUBSCRIPTION_PAYMENTS':
      return { ...state, loadingPayments: true };
    
    case 'LOADING_SUBSCRIPTION_ACTIVITY':
      return { ...state, loadingActivity: true };
    
    case 'LOADING_SUBSCRIPTION_USAGE_HISTORY':
      return { ...state, loadingUsageHistory: true };

    // Agent Management Reducers
    case 'SET_ADMIN_AGENTS':
      return { 
        ...state, 
        adminAgents: payload.agents || [],
        agentsPagination: payload.pagination || state.agentsPagination,
        loading: false 
      };

    case 'SET_AGENT_STATS':
      return { ...state, agentStats: payload, loading: false };

    case 'SET_SELECTED_AGENT':
      return { ...state, selectedAgent: payload };

    case 'SET_AGENT_LOGS':
      return { ...state, agentLogs: payload.logs || [], loading: false };

    case 'SET_AGENT_CALLS':
      return { ...state, agentCalls: payload.calls || [], loading: false };

    case 'SET_PERFORMANCE_METRICS':
      return { ...state, performanceMetrics: payload, loading: false };

    case 'SET_COST_ANALYTICS':
      return { ...state, costAnalytics: payload, loading: false };

    // System Monitoring Reducers
    case 'SET_SYSTEM_HEALTH':
      return { ...state, systemHealth: payload, loading: false };

    case 'SET_SYSTEM_SERVICES':
      return { ...state, systemServices: payload, loading: false };

    case 'SET_SYSTEM_METRICS':
      return { ...state, systemMetrics: payload.historical || [], loading: false };

    case 'SET_SYSTEM_ALERTS':
      return { ...state, systemAlerts: payload.alerts || [], loading: false };

    case 'SET_SYSTEM_PERFORMANCE':
      return { ...state, systemPerformance: payload.current || {}, loading: false };

    case 'SET_AGENT_PERFORMANCE':
      return { ...state, agentPerformance: payload, loading: false };

    // Dashboard Reducer
    case 'SET_ADMIN_DASHBOARD':
      // If payload only contains agentHealth, merge it with existing data
      if (payload.agentHealth && Object.keys(payload).length === 1) {
        return { 
          ...state, 
          adminDashboard: {
            ...state.adminDashboard,
            agentHealth: payload.agentHealth
          },
          loading: false 
        };
      }
      // Otherwise replace entire dashboard data
      return { ...state, adminDashboard: payload, loading: false };

    // Loading State
    case 'SET_ADMIN_LOADING':
      return { ...state, loading: payload.isLoading, loadingMessage: payload.message || '' };

    // Analytics Reducers
    case 'GET_ANALYTICS_DATA':
      return { ...state, loading: true };
      
    case 'GET_CUSTOM_REPORT':
      // Use reportGenerating instead of loading for reports
      return { ...state, reportGenerating: true };
      
    case 'EXPORT_REPORT':
      // Don't set any loading state for export
      return state;

    case 'SET_ANALYTICS_DATA':
      return { ...state, analyticsData: payload, loading: false };

    case 'SET_CUSTOM_REPORTS':
      return { ...state, customReports: payload, reportGenerating: false };
      
    case 'REPORT_GENERATION_COMPLETE':
      return { ...state, reportGenerating: false };
      
    case 'REPORT_GENERATION_FAILED':
      return { ...state, reportGenerating: false };
      
    case 'EXPORT_REPORT_SUCCESS':
    case 'EXPORT_REPORT_FAILURE':
      return { ...state, reportGenerating: false };

    // Security & Compliance Reducers
    case 'GET_AUDIT_LOGS':
    case 'GET_SECURITY_SETTINGS':
    case 'UPDATE_SECURITY_SETTINGS':
    case 'ADD_CREDITS':
    case 'GET_ALL_WORKSPACES':
      return { ...state, loading: true };

    case 'GET_CREDIT_HISTORY':
      return { ...state, creditHistoryLoading: true };

    case 'SET_AUDIT_LOGS':
      return { 
        ...state, 
        auditLogs: payload.logs || [],
        auditLogsPagination: payload.pagination || state.auditLogsPagination,
        loading: false 
      };
      
    case 'SET_CREDIT_HISTORY':
      return {
        ...state,
        creditHistory: payload || state.creditHistory,
        creditHistoryLoading: false
      };

    case 'SET_USER_ACCESS_TYPE':
      return {
        ...state,
        userAccessType: payload || state.userAccessType
      };

    case 'SET_ALL_WORKSPACES':
      return {
        ...state,
        workspaces: payload || [],
        loading: false
      };

    case 'GET_WORKSPACE_CREDITS':
      return { ...state, loading: true };

    case 'SET_WORKSPACE_CREDITS':
      return {
        ...state,
        workspaceCredits: payload || { workspaces: [], stats: {} },
        loading: false
      };

    case 'SET_SECURITY_SETTINGS':
      return { 
        ...state, 
        securitySettings: payload.settings || {},
        apiKeys: payload.apiKeys || [],
        ipWhitelist: payload.ipWhitelist || [],
        loading: false 
      };

    // Compliance Reducers
    case 'SET_COMPLIANCE_STATUS':
      return {
        ...state,
        complianceStatus: payload.compliance || {},
        loading: false
      };

    case 'SET_DATA_REQUESTS':
      return {
        ...state,
        complianceStatus: {
          ...state.complianceStatus,
          dataRequests: payload.requests || []
        },
        loading: false
      };

    // Email Templates Reducers
    case 'GET_EMAIL_TEMPLATES':
    case 'GET_TEMPLATE_STATS':
    case 'CREATE_EMAIL_TEMPLATE':
    case 'UPDATE_EMAIL_TEMPLATE':
    case 'DELETE_EMAIL_TEMPLATE':
    case 'ACTIVATE_TEMPLATE':
    case 'DEACTIVATE_TEMPLATE':
    case 'DUPLICATE_TEMPLATE':
    case 'BULK_ACTIVATE_TEMPLATES':
    case 'BULK_DEACTIVATE_TEMPLATES':
    case 'BULK_DELETE_TEMPLATES':
    case 'BULK_CHANGE_TEMPLATE_CATEGORY':
    case 'BULK_EXPORT_TEMPLATES':
      return { ...state, loading: true };

    case 'SET_EMAIL_TEMPLATES':
      return { 
        ...state, 
        emailTemplates: payload.templates || [],
        templatesPagination: payload.pagination || state.templatesPagination,
        loading: false 
      };

    case 'SET_TEMPLATE_STATS':
      return { ...state, templateStats: payload, loading: false };

    // Activity Logs Reducers
    case 'GET_ACTIVITY_LOGS':
    case 'GET_ACTIVITY_STATS':
    case 'CLEAR_OLD_LOGS':
    case 'ARCHIVE_LOGS':
      return { ...state, loading: true };

    case 'SET_ACTIVITY_LOGS':
      return { 
        ...state, 
        activityLogs: payload.logs || [],
        activityLogsPagination: payload.pagination || state.activityLogsPagination,
        loading: false 
      };

    case 'SET_ACTIVITY_STATS':
      return { ...state, activityStats: payload, loading: false };

    // Workspace Details Sidebar Reducers
    case 'GET_WORKSPACE_MEMBERS':
      return { ...state, loadingMembers: true };
    
    case 'SET_WORKSPACE_MEMBERS':
      return { ...state, workspaceMembers: payload.members || [], loadingMembers: false };
    
    case 'GET_WORKSPACE_CONTACTS':
      return { ...state, loadingContacts: true };
    
    case 'SET_WORKSPACE_CONTACTS':
      return { ...state, workspaceContacts: payload.contacts || [], loadingContacts: false };
    
    case 'GET_WORKSPACE_DEALS':
      return { ...state, loadingDeals: true };
    
    case 'SET_WORKSPACE_DEALS':
      return { ...state, workspaceDeals: payload.deals || [], loadingDeals: false };
    
    case 'GET_WORKSPACE_AGENTS':
      return { ...state, loadingAgents: true };
    
    case 'SET_WORKSPACE_AGENTS':
      return { ...state, workspaceAgents: payload.agents || [], loadingAgents: false };
    
    case 'GET_WORKSPACE_ACTIVITY':
      console.log('REDUCER: GET_WORKSPACE_ACTIVITY - Setting loadingActivity to true');
      return { ...state, loadingActivity: true, workspaceActivity: [] };

    case 'SET_WORKSPACE_ACTIVITY':
      console.log('REDUCER: SET_WORKSPACE_ACTIVITY received');
      console.log('Payload is array?', Array.isArray(payload));
      console.log('Payload length:', Array.isArray(payload) ? payload.length : 'not an array');

      const newState = {
        ...state,
        workspaceActivity: Array.isArray(payload) ? payload : [],
        loadingActivity: false
      };

      console.log('New loadingActivity state:', newState.loadingActivity);
      console.log('New workspaceActivity length:', newState.workspaceActivity.length);

      return newState;
    
    case 'GET_OWNER_WORKSPACES':
      return { ...state, loadingOwnerWorkspaces: true };
    
    case 'SET_OWNER_WORKSPACES':
      return { 
        ...state, 
        ownerWorkspaces: payload.workspaces || [], 
        ownerWorkspacesData: payload,
        loadingOwnerWorkspaces: false 
      };
    
    case 'GET_ACCOUNT_AGENTS':
      return { ...state, loadingAccountAgents: true };
    
    case 'SET_ACCOUNT_AGENTS':
      return { 
        ...state, 
        accountAgentsData: payload,
        loadingAccountAgents: false 
      };
    
    case 'GET_WORKSPACE_PAYMENT_HISTORY':
      return { ...state, loadingPaymentHistory: true };
    
    case 'SET_WORKSPACE_PAYMENT_HISTORY':
      return { 
        ...state, 
        workspacePaymentHistory: payload,
        loadingPaymentHistory: false 
      };

    // System Configuration Reducers
    case 'GET_SYSTEM_CONFIG':
    case 'SAVE_SYSTEM_CONFIG':
    case 'RESET_CONFIG_CATEGORY':
    case 'TEST_SERVICE_CONNECTION':
      return { ...state, loading: true };

    case 'SET_SYSTEM_CONFIG':
      return { ...state, systemConfig: payload, loading: false };

    // Backup & Restore Reducers
    case 'GET_BACKUPS':
    case 'GET_BACKUP_STATS':
    case 'GET_RESTORE_HISTORY':
    case 'CREATE_BACKUP':
    case 'RESTORE_BACKUP':
    case 'DELETE_BACKUP':
    case 'DOWNLOAD_BACKUP':
    case 'UPLOAD_BACKUP':
    case 'CLEANUP_OLD_BACKUPS':
    case 'VERIFY_ALL_BACKUPS':
    case 'REBUILD_BACKUP_INDEX':
      return { ...state, loading: true };

    case 'SET_BACKUPS':
      return { 
        ...state, 
        backups: payload.backups || [],
        backupsPagination: payload.pagination || state.backupsPagination,
        loading: false 
      };

    case 'SET_BACKUP_STATS':
      return { ...state, backupStats: payload, loading: false };

    case 'SET_RESTORE_HISTORY':
      return { 
        ...state, 
        restoreHistory: payload.restores || [],
        restoreHistoryPagination: payload.pagination || state.restoreHistoryPagination,
        loading: false 
      };

    // User Details Sidebar Reducers
    case 'GET_USER_WORKSPACES':
      return { ...state, loadingUserWorkspaces: true };

    case 'SET_USER_WORKSPACES':
      return { ...state, userWorkspaces: payload, loadingUserWorkspaces: false };

    case 'GET_USER_BILLING_HISTORY':
      return { ...state, loadingUserBilling: true };

    case 'SET_USER_BILLING_HISTORY':
      return { ...state, userBillingHistory: payload, loadingUserBilling: false };

    case 'GET_USER_SECURITY_LOGS':
      return { ...state, loadingUserSecurity: true };

    case 'SET_USER_SECURITY_LOGS':
      return { ...state, userSecurityLogs: payload, loadingUserSecurity: false };

    case 'GET_USER_ACTIVITIES':
      return { ...state, loadingUserActivities: true };

    case 'SET_USER_ACTIVITIES':
      return { ...state, activityLogs: payload, loadingUserActivities: false };

    // Additional Bulk Actions
    case 'DELETE_BULK_USERS':
    case 'SEND_BULK_EMAIL':
    case 'BULK_EMAIL_SUBSCRIPTIONS':
    case 'BULK_RESET_WORKSPACE_USAGE':
    case 'BULK_DELETE_WORKSPACES':
    case 'BULK_ASSIGN_WORKSPACE':
    case 'SEND_WORKSPACE_NOTIFICATION':
      return { ...state, loading: true };

    // User Feedback Management
    case 'GET_ADMIN_FEEDBACK':
    case 'GET_FEEDBACK_STATS':
      return { ...state, loadingFeedback: true };

    case 'SET_ADMIN_FEEDBACK':
      return {
        ...state,
        adminFeedback: payload.feedback || [],
        feedbackPagination: payload.pagination || state.feedbackPagination,
        loadingFeedback: false
      };

    case 'SET_FEEDBACK_STATS':
      return {
        ...state,
        feedbackStats: payload,
        loadingFeedback: false
      };

    case 'GET_ADMIN_FEEDBACK_BY_ID':
      return { ...state, loadingFeedback: true };

    case 'SET_SELECTED_FEEDBACK':
      return {
        ...state,
        selectedFeedback: payload,
        loadingFeedback: false
      };

    case 'UPDATE_FEEDBACK_STATUS':
    case 'ADD_ADMIN_FEEDBACK_RESPONSE':
      return { ...state, loadingFeedback: true };

    case 'FEEDBACK_UPDATED':
      return {
        ...state,
        selectedFeedback: payload,
        adminFeedback: state.adminFeedback.map(fb =>
          fb._id === payload._id ? payload : fb
        ),
        loadingFeedback: false
      };

    default:
      return state;
  }
}